import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site-config';

function formatViews(views: number): string {
  if (views >= 10000) return (views / 10000).toFixed(1) + '万';
  return views.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = useMemo(() => {
    return articles.find((a) => a.id === articleId) || articles[0];
  }, [articleId]);

  const category = useMemo(() => {
    return categories.find((c) => c.slug === article.category);
  }, [article]);

  const relatedArticles = useMemo(() => {
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  const paragraphs = article.content.split('\n\n').filter((p) => p.trim());

  return (
    <>
      <Header />

      {/* SEO: Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.image,
          "datePublished": article.date,
          "dateModified": article.date,
          "author": { "@type": "Organization", "name": "51吃瓜官网" },
          "publisher": {
            "@type": "Organization",
            "name": "51吃瓜官网",
            "logo": { "@type": "ImageObject", "url": `${siteConfig.url}/logo.png` },
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${siteConfig.url}/article/${article.id}`,
          },
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": siteConfig.url },
            { "@type": "ListItem", "position": 2, "name": category?.name || '爆料', "item": `${siteConfig.url}/category/${category?.slug || ''}` },
            { "@type": "ListItem", "position": 3, "name": article.title, "item": `${siteConfig.url}/article/${article.id}` },
          ],
        })}
      </script>

      <main className="pt-16">
        {/* Article Hero Image */}
        <section className="relative w-full h-[280px] md:h-[420px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-6 md:pb-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-3">
                <Link
                  to={`/category/${category?.slug || ''}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500 text-background-50 text-xs font-label font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {category?.name || '爆料'}
                </Link>
                {article.tag && (
                  <span className={`inline-block px-2.5 py-0.5 text-xs font-label font-semibold text-background-50 ${article.tagColor} rounded-full`}>
                    {article.tag}
                  </span>
                )}
                <span className="text-xs text-background-50/70">
                  {article.date}
                </span>
              </div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 leading-snug">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <nav aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                </li>
                <span className="text-foreground-300">/</span>
                <li>
                  <Link to={`/category/${category?.slug || ''}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                    {category?.name || '爆料'}
                  </Link>
                </li>
                <span className="text-foreground-300">/</span>
                <li className="text-foreground-700 font-medium line-clamp-1 max-w-[200px] md:max-w-md">
                  {article.title}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6 md:mb-8 pb-6 md:pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)}阅读
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-chat-3-line"></i>
                {formatViews(article.comments)}评论
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-user-line"></i>
                51吃瓜编辑部
              </span>
            </div>

            {/* Excerpt */}
            <p className="text-base md:text-lg text-foreground-600 font-medium leading-relaxed mb-6 md:mb-8 p-4 md:p-5 bg-accent-50/60 border-l-4 border-accent-400 rounded-r-lg">
              {article.excerpt}
            </p>

            {/* Content */}
            <div className="prose-content">
              {paragraphs.map((para, idx) => (
                <p
                  key={idx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5 md:mb-6"
                >
                  {para}
                </p>
              ))}
            </div>

            {/* Share buttons */}
            <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-background-200/70">
              <p className="text-sm font-label font-semibold text-foreground-700 mb-3">
                分享给好友
              </p>
              <div className="flex items-center gap-2">
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                  <i className="ri-qq-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                  <i className="ri-link text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <article key={ra.id} className="group">
                    <Link
                      to={`/article/${ra.id}`}
                      className="block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 cursor-pointer hover:border-background-300/80"
                    >
                      <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-label text-primary-500 font-medium">
                            {ra.categoryName}
                          </span>
                          <span className="text-xs text-foreground-400">{ra.date}</span>
                        </div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}